import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { supabase, useAuth } from 'hooks/useAuth';
import { useToast } from 'hooks/useToast';

import { Database } from 'libs/supabaseTypes';
import {
    PILLARS,
    STATUS,
    PURSUIT_YEARS_EXPERIENCE_OPTIONS,
} from 'libs/constants';

import { SelectProduct } from 'components/ui/SelectProduct';
import { IconRadio, IconRadioItem } from 'components/ui/IconRadio';
import { Button } from 'components/ui/Button';
import { Loader } from 'components/ui/Loader';
import { RequiredAsterisk } from 'components/ui/RequiredAsterisk';

import GrandPursuitLogo from 'assets/grand-pursuit-logo.png';

import { colors, Fonts, spacing, transparencies } from 'styles/theme';
import {
    Bumper,
    Card,
    CardBottomButtonWrapper,
    FullBleedScroller,
    ProgressBar,
    ProgressBarWrapper,
} from 'styles/shared';
import { TextInput } from 'components/ui/TextInput';
import { Select } from 'components/ui/Select';

declare global {
    interface Window {
        fbq?: (...args: unknown[]) => void;
        gtag?: (...args: unknown[]) => void;
    }
}

export interface Product {
    id: string;
    name: string;
    description: string;
    prices: Array<{
        id: string;
        unit_amount: number;
        currency: string;
        recurring?: {
            interval: string;
        };
    }>;
    metadata: {
        rank?: string;
        tagline?: string;
        testimonial?: string;
        link?: string;
        disclaimer?: string;
        soldOutText?: string;
    };
}

export type LeadData = Database['public']['Tables']['leads']['Row'];

type PursuitStep = 'submit-application' | 'completed';
type YearsExperienceOptions = (typeof PURSUIT_YEARS_EXPERIENCE_OPTIONS)[number];

export const PursuitApplication = () => {
    const { userId } = useAuth();
    const { showErrorToast } = useToast();

    const [currentStep, setCurrentStep] = useState<PursuitStep | null>(null);
    const [lead, setLead] = useState<LeadData | null>(null);
    const [location, setLocation] = useState<string>('');
    const [title, setTitle] = useState<string>('');
    const [company, setCompany] = useState<string>('');
    const [yearsExperience, setYearsExperience] =
        useState<YearsExperienceOptions | null>(null);
    const [linkedIn, setLinkedIn] = useState<string>('');
    const [instagram, setInstagram] = useState<string>('');
    const [phoneNumber, setPhoneNumber] = useState<string>('');
    const [referrer, setReferrer] = useState<string>('');
    const [products, setProducts] = useState<Product[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isLoadingProducts, setIsLoadingProducts] = useState<boolean>(true);
    const [loadingSelectedProductId, setLoadingSelectedProductString] =
        useState<string>('');
    const [
        loadingSelectedPillarDescription,
        setLoadingSelectedPillarDescription,
    ] = useState<string>('');
    const [imaginedFuture, setImaginedFuture] = useState<string>('');
    const [children, setChildren] = useState<string>('');
    const [other, setOther] = useState<string>('');
    const [isSubmittingPursuit, setIsSubmittingPursuit] =
        useState<boolean>(false);

    const selectedProduct = useMemo(() => {
        if (!lead?.selected_product_id) {
            return null;
        }

        const product = products.find(
            (product) => product.id === lead.selected_product_id
        );
        return product ?? null;
    }, [lead?.selected_product_id, products]);

    useEffect(() => {
        if (lead == null) {
            return;
        }

        if (lead.status === STATUS.New || lead.status === STATUS.InProgress) {
            setCurrentStep('submit-application');
        } else if (lead.status === STATUS.Completed) {
            setCurrentStep('completed');
        }
        // eslint-disable-next-line react-hooks-addons/no-unused-deps
    }, [lead, selectedProduct]);

    const getLead = useCallback(async () => {
        const { data, error } = await supabase
            .from('leads')
            .select('*')
            .eq('profile_id', userId);

        if (error == null && data?.[0] != null) {
            setLead(data[0]);
            setIsLoading(false);
        } else {
            showErrorToast('Unable to fetch membership data');
            setIsLoading(false);
        }
    }, [showErrorToast, userId]);

    const fetchProducts = useCallback(async () => {
        const { data, error } = await supabase.functions.invoke(
            'get-stripe-pursuit-products'
        );

        if (error == null) {
            setProducts(data.products);
            setIsLoadingProducts(false);
        } else {
            showErrorToast('Unable to fetch pursuit products');
        }
    }, [showErrorToast]);

    useEffect(() => {
        if (userId !== '') {
            getLead();
            fetchProducts();
        }
    }, [fetchProducts, getLead, userId]);

    const onLocationInput = useCallback(
        (event: React.FormEvent<HTMLInputElement>) => {
            const target = event.target as HTMLInputElement;
            setLocation(target.value);
        },
        []
    );

    const onTitleInput = useCallback(
        (event: React.FormEvent<HTMLInputElement>) => {
            const target = event.target as HTMLInputElement;
            setTitle(target.value);
        },
        []
    );

    const onCompanyInput = useCallback(
        (event: React.FormEvent<HTMLInputElement>) => {
            const target = event.target as HTMLInputElement;
            setCompany(target.value);
        },
        []
    );

    const onChangeYearsExperience = useCallback(
        (event: React.ChangeEvent<HTMLSelectElement>) => {
            const target = event.target as HTMLSelectElement;
            setYearsExperience(target.value as YearsExperienceOptions);
        },
        []
    );

    const onLinkedInInput = useCallback(
        (event: React.FormEvent<HTMLInputElement>) => {
            const target = event.target as HTMLInputElement;
            setLinkedIn(target.value);
        },
        []
    );

    const onInstagramInput = useCallback(
        (event: React.FormEvent<HTMLInputElement>) => {
            const target = event.target as HTMLInputElement;
            setInstagram(target.value);
        },
        []
    );

    const onPhoneNumberInput = useCallback(
        (event: React.FormEvent<HTMLInputElement>) => {
            const target = event.target as HTMLInputElement;
            setPhoneNumber(target.value);
        },
        []
    );

    const onReferrerInput = useCallback(
        (event: React.FormEvent<HTMLInputElement>) => {
            const target = event.target as HTMLInputElement;
            setReferrer(target.value);
        },
        []
    );

    const onSelectProduct = useCallback(
        (product: Product) => async () => {
            setLoadingSelectedProductString(product.id);

            const { error } = await supabase
                .from('leads')
                .update({
                    selected_product_id:
                        product?.id ?? lead?.selected_product_id,
                })
                .eq('id', lead?.id ?? '');

            if (error == null) {
                await getLead();
                setLoadingSelectedProductString('');
            } else {
                showErrorToast('Unable to update membership data');
                setLoadingSelectedProductString('');
            }
        },
        [getLead, lead?.id, lead?.selected_product_id, showErrorToast]
    );

    const onSelectPillar = useCallback(
        (pillarDescription: string) => async () => {
            setLoadingSelectedPillarDescription(pillarDescription);

            const { error } = await supabase
                .from('leads')
                .update({
                    core_interest: pillarDescription,
                })
                .eq('id', lead?.id ?? '');

            if (error == null) {
                await getLead();
                setLoadingSelectedPillarDescription('');
            } else {
                showErrorToast('Unable to update membership data');
                setLoadingSelectedPillarDescription('');
            }
        },
        [getLead, lead?.id, showErrorToast]
    );

    const onImaginedFutureInput = useCallback(
        (event: React.FormEvent<HTMLInputElement>) => {
            const target = event.target as HTMLInputElement;
            setImaginedFuture(target.value);
        },
        []
    );

    const onChildrenInput = useCallback(
        (event: React.FormEvent<HTMLInputElement>) => {
            const target = event.target as HTMLInputElement;
            setChildren(target.value);
        },
        []
    );
    const onOtherInput = useCallback(
        (event: React.FormEvent<HTMLInputElement>) => {
            const target = event.target as HTMLInputElement;
            setOther(target.value);
        },
        []
    );

    const sendTrackingEvents = useCallback(() => {
        if (typeof window?.fbq !== 'undefined') {
            window.fbq('track', 'SubmitApplication');
        }
        if (typeof window?.gtag !== 'undefined') {
            window.gtag('event', 'conversion', {
                send_to: 'AW-17826738146/AdazCJn_0O4bEOLfubRC',
            });
        }
    }, []);

    const onContinueApplication = useCallback(
        async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();

            if (isSubmittingPursuit) {
                return;
            }
            setIsSubmittingPursuit(true);

            const { error: profileError } = await supabase
                .from('profiles')
                .update({
                    linkedin: linkedIn,
                })
                .eq('id', userId);

            if (profileError != null) {
                showErrorToast(profileError.message);
                setIsSubmittingPursuit(false);
                return;
            }

            const { error: leadError } = await supabase
                .from('leads')
                .update({
                    referrer,
                    title,
                    company,
                    location,
                    metadata: {
                        ...JSON.parse(JSON.stringify(lead?.metadata ?? {})),
                        years_experience: yearsExperience,
                        instagram,
                        phone_number: phoneNumber,
                    },
                    status: STATUS.InProgress,
                })
                .eq('id', lead?.id ?? '');

            setIsSubmittingPursuit(false);

            if (leadError == null) {
                setIsLoading(true);
                getLead();
            } else {
                showErrorToast(leadError.message);
            }
        },
        [
            company,
            getLead,
            instagram,
            isSubmittingPursuit,
            lead?.id,
            lead?.metadata,
            linkedIn,
            location,
            phoneNumber,
            referrer,
            showErrorToast,
            title,
            userId,
            yearsExperience,
        ]
    );

    const onSubmitApplication = useCallback(
        async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();

            if (isSubmittingPursuit) {
                return;
            }
            setIsSubmittingPursuit(true);

            const { error: profileError } = await supabase
                .from('profiles')
                .update({
                    imagined_future: imaginedFuture,
                })
                .eq('id', userId);

            if (profileError != null) {
                showErrorToast(profileError.message);
                setIsSubmittingPursuit(false);
                return;
            }

            const { error: leadError } = await supabase
                .from('leads')
                .update({
                    notes: other,
                    metadata: {
                        ...JSON.parse(JSON.stringify(lead?.metadata ?? {})),
                        children,
                    },
                    status: STATUS.Completed,
                })
                .eq('id', lead?.id ?? '');

            setIsSubmittingPursuit(false);

            if (leadError == null) {
                setIsLoading(true);
                getLead();
                sendTrackingEvents();
            } else {
                showErrorToast(leadError.message);
            }
        },
        [
            children,
            getLead,
            imaginedFuture,
            isSubmittingPursuit,
            lead?.id,
            lead?.metadata,
            other,
            sendTrackingEvents,
            showErrorToast,
            userId,
        ]
    );

    const progressPercentage = useMemo(() => {
        let percentage = 40;
        if (lead?.status === STATUS.InProgress) {
            percentage += 40;
        }
        return percentage;
    }, [lead?.status]);

    const sortedProducts = useMemo(
        () =>
            products.find((product) => product.metadata.rank == null) != null
                ? products
                : products.sort((a, b) => {
                      const rankA = parseInt(a.metadata.rank ?? '0', 10);
                      const rankB = parseInt(b.metadata.rank ?? '0', 10);
                      return rankA - rankB;
                  }),
        [products]
    );

    const pillarsToRadioItems: Array<IconRadioItem> = useMemo(
        () =>
            PILLARS.map(({ description, journey }) => ({
                key: description,
                label: description,
                sublabel: journey,
            })),
        []
    );

    return isLoading || currentStep == null ? (
        <Loader $color={colors.grandGreen} />
    ) : (
        <FullBleedScroller
            // TODO: replace inline styles
            style={
                currentStep === 'completed'
                    ? { padding: '0 32px', maxWidth: '560px' }
                    : {}
            }
        >
            <Bumper />
            <div>
                <img
                    src={GrandPursuitLogo}
                    width={140}
                    alt="grand-pursuit-logo"
                />
            </div>
            <Bumper />
            {currentStep === 'completed' ? (
                <>
                    <Fonts.Heading7 $textAlign="center">{`Thank you!`}</Fonts.Heading7>
                    <Bumper />
                    <Fonts.Medium $textAlign="center">
                        {`Your application has been received, and we're excited to learn about your journey. Our team will review your details and be in touch soon with next steps for joining The Grand Pursuit.`}
                    </Fonts.Medium>
                    <Bumper />
                </>
            ) : (
                <>
                    <Card
                        $width="720px"
                        $backgroundColor={transparencies.white60}
                        $isMobileOverflowHidden
                    >
                        <Fonts.Heading6
                            $color={colors.mountainAsh}
                            $textAlign="center"
                        >{`Complete your application`}</Fonts.Heading6>
                        <Bumper $minHeight={spacing.xLarge} />
                        <ProgressBarWrapper>
                            <ProgressBar $width={progressPercentage} />
                        </ProgressBarWrapper>
                        <Bumper $minHeight={spacing.xxLarge} />
                        {lead?.status === STATUS.New ? (
                            <form onSubmit={onContinueApplication}>
                                <TextInput
                                    id="location"
                                    name="location"
                                    value={location}
                                    label="Where are you located?"
                                    onInput={onLocationInput}
                                    required
                                    $backgroundColor={
                                        transparencies.darkJungle5
                                    }
                                />
                                <Bumper $minHeight={spacing.xxLarge} />
                                <TextInput
                                    id="title"
                                    name="title"
                                    value={title}
                                    label="What's your current job title?"
                                    onInput={onTitleInput}
                                    required
                                    $backgroundColor={
                                        transparencies.darkJungle5
                                    }
                                />
                                <Bumper $minHeight={spacing.xxLarge} />
                                <TextInput
                                    id="company"
                                    name="company"
                                    value={company}
                                    label="What's the name of your company?"
                                    onInput={onCompanyInput}
                                    required
                                    $backgroundColor={
                                        transparencies.darkJungle5
                                    }
                                />
                                <Bumper $minHeight={spacing.xxLarge} />
                                <Select
                                    onInput={onChangeYearsExperience}
                                    value={yearsExperience ?? ''}
                                    label="How many years of work experience do you have?"
                                    required
                                    $backgroundColor={
                                        transparencies.darkJungle5
                                    }
                                >
                                    <option value="" label="Select" hidden />
                                    {PURSUIT_YEARS_EXPERIENCE_OPTIONS.map(
                                        (yearsExperinceOption) => (
                                            <option
                                                key={yearsExperinceOption}
                                                value={yearsExperinceOption}
                                            >
                                                {yearsExperinceOption}
                                            </option>
                                        )
                                    )}
                                </Select>
                                <Bumper $minHeight={spacing.xxLarge} />
                                <TextInput
                                    id="linkedin"
                                    name="linkedin"
                                    value={linkedIn}
                                    label="What's your LinkedIn profile URL?"
                                    onInput={onLinkedInInput}
                                    $backgroundColor={
                                        transparencies.darkJungle5
                                    }
                                />
                                <Bumper $minHeight={spacing.xxLarge} />
                                <TextInput
                                    id="instagram"
                                    name="instagram"
                                    value={instagram}
                                    label="What's your Instagram handle?"
                                    onInput={onInstagramInput}
                                    $backgroundColor={
                                        transparencies.darkJungle5
                                    }
                                />
                                <Bumper $minHeight={spacing.xxLarge} />
                                <TextInput
                                    id="phone-number"
                                    name="phone-number"
                                    value={phoneNumber}
                                    label="Do prefer text? If so, what’s your number?"
                                    onInput={onPhoneNumberInput}
                                    $backgroundColor={
                                        transparencies.darkJungle5
                                    }
                                />
                                <Bumper $minHeight={spacing.xxLarge} />
                                <TextInput
                                    id="referrer"
                                    name="referrer"
                                    value={referrer}
                                    label="Were you referred by a friend or company to The Grand Pursuit?"
                                    onInput={onReferrerInput}
                                    $backgroundColor={
                                        transparencies.darkJungle5
                                    }
                                />
                                <Bumper $minHeight={spacing.xxxLarge} />
                                <CardBottomButtonWrapper
                                    $backgroundColor="transparent"
                                    $shouldCenterOnMobile
                                >
                                    <Button
                                        type="submit"
                                        $isLoading={isSubmittingPursuit}
                                        $isDisabled={
                                            location === '' ||
                                            title === '' ||
                                            company === '' ||
                                            yearsExperience == null
                                        }
                                    >{`Continue`}</Button>
                                </CardBottomButtonWrapper>
                            </form>
                        ) : (
                            <form onSubmit={onSubmitApplication}>
                                <Fonts.Medium $isOpenSansSemiBold>
                                    {`Your journey awaits`}
                                </Fonts.Medium>
                                <Bumper $minHeight={spacing.xxxSmall} />
                                <Fonts.Medium>
                                    {`Select the membership option that you're interested in`}
                                    <RequiredAsterisk />
                                </Fonts.Medium>
                                <Bumper />
                                <SelectProduct
                                    products={sortedProducts}
                                    selectedProduct={selectedProduct}
                                    onSelectProduct={onSelectProduct}
                                    isLoadingProducts={isLoadingProducts}
                                    loadingSelectedProductId={
                                        loadingSelectedProductId
                                    }
                                />
                                <Bumper $minHeight={spacing.xxLarge} />
                                <Fonts.Medium
                                    $isOpenSansSemiBold
                                >{`What drives your Pursuit?`}</Fonts.Medium>
                                <Bumper $minHeight={spacing.xxxSmall} />
                                <Fonts.Medium>
                                    {`Choose the goal that feels most important to you right now`}
                                    <RequiredAsterisk />
                                </Fonts.Medium>
                                <IconRadio
                                    items={pillarsToRadioItems}
                                    onSelectItem={onSelectPillar}
                                    selectedKey={lead?.core_interest ?? ''}
                                    loadingKey={
                                        loadingSelectedPillarDescription
                                    }
                                />
                                <Bumper $minHeight={spacing.xxLarge} />
                                <TextInput
                                    id="imagined-future"
                                    name="imagined-future"
                                    value={imaginedFuture}
                                    label="Your vision ahead"
                                    subLabel="When you close your eyes and imagine yourself 6 months from now, what do you see?"
                                    isTextArea
                                    onInput={onImaginedFutureInput}
                                    required
                                    $backgroundColor={
                                        transparencies.darkJungle5
                                    }
                                    $isAltText
                                />
                                <Bumper $minHeight={spacing.xxLarge} />
                                <TextInput
                                    id="children"
                                    name="children"
                                    value={children}
                                    label="Your children who inspire you"
                                    subLabel="What are the names and ages of your children? (Optional - helps us personalize your experience)"
                                    isTextArea
                                    onInput={onChildrenInput}
                                    $backgroundColor={
                                        transparencies.darkJungle5
                                    }
                                    $isAltText
                                />
                                <Bumper $minHeight={spacing.xxLarge} />
                                <TextInput
                                    id="other"
                                    name="other"
                                    value={other}
                                    label="Your story"
                                    subLabel="What else makes your journey unique? Share anything else you’d like us to know"
                                    isTextArea
                                    onInput={onOtherInput}
                                    $backgroundColor={
                                        transparencies.darkJungle5
                                    }
                                    $isAltText
                                />
                                <Bumper />
                                <Bumper $minHeight={spacing.xLarge} />
                                <CardBottomButtonWrapper
                                    $backgroundColor="transparent"
                                    $shouldCenterOnMobile
                                >
                                    <Button
                                        type="submit"
                                        $isLoading={isSubmittingPursuit}
                                        $isDisabled={
                                            selectedProduct == null ||
                                            lead?.core_interest == null ||
                                            imaginedFuture === ''
                                        }
                                    >{`Submit Application`}</Button>
                                </CardBottomButtonWrapper>
                            </form>
                        )}
                    </Card>
                    <Bumper $minHeight={spacing.xxxLarge} $shouldHideOnMobile />
                    <Bumper $minHeight={spacing.xxxLarge} />
                </>
            )}
        </FullBleedScroller>
    );
};
